import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  Search,
  LogOut,
  Building2,
  Loader2,
  Save,
  RotateCcw,
  CheckCircle2,
  XCircle,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Check,
  MoreVertical,
  Trash2,
  LockOpen,
  Lock,
  KeyRound,
  Eye,
  EyeOff,
  Coins,
  AlertTriangle,
} from "lucide-react";
import ConfirmDialog, {
  type ConfirmState,
} from "../../components/ConfirmDialog";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { PermissionGridEditor } from "../../components/PermissionGridEditor";
import {
  blankPermissions,
  fullAccessPermissions,
  panelCatalogForPlan,
  restrictPermissionsToCatalog,
  enabledCountForCatalog,
} from "../../utils/accessPermissions";
import type { PanelPermission } from "../../api/access";
import type { PanelLevel } from "../../config/panelCatalog";
import {
  listAdmins,
  getAdminPermissions,
  setAdminPermissions,
  clearAdminPermissions,
  setAdminStatus,
  deleteAdmin,
  resetAdminPassword,
  getAdminSubscription,
  setAdminPlan,
  getAdminCredits,
  setAdminCredits,
  type SuperadminAdmin,
  type AdminSubscription,
  type AdminCredits,
  type WorkspacePlan,
} from "../../api/superadmin";
import { lockLogin, unlockLogin } from "../../api/auth";
// The cross-org queue of questions Lio couldn't answer. Its own file: this one
// is already long, and the two views share nothing but the admin list.
import LioIssueQueue from "./LioIssueQueue";
// The toolbar's dropdown shape, shared with the queue above.
import { FilterDropdown, type FilterOption } from "./FilterDropdown";

// The three workspace plans, in upgrade order. "starter" is the free plan,
// "professional" unlocks everything, "enterprise" is the custom plan whose
// panels the superadmin picks in the Access grid.
const PLAN_OPTIONS: {
  value: WorkspacePlan;
  label: string;
  blurb: string;
}[] = [
  {
    value: "starter",
    label: "Free",
    blurb: "Limited workspace — core HR panels only, up to 2 employees.",
  },
  {
    value: "professional",
    label: "Professional",
    blurb: "Full access — every panel unlocked, unlimited employees.",
  },
  {
    value: "enterprise",
    label: "Custom",
    blurb: "Unlimited employees; you pick the panels under Access.",
  },
];

// What add_employee actually enforces (routers/employee.py): a limited
// workspace — Free (starter), or an enterprise sign-up still in sales_pending —
// is capped at 2; Professional and granted Custom are unlimited and any stored
// employee_limit on those rows is ignored. Mirrored here so the dialog never
// reports a limit the API doesn't apply.
const employeeLimitLabel = (plan?: string | null, status?: string | null) =>
  plan === "starter" || status === "sales_pending"
    ? "2 employees"
    : "unlimited employees";

// The same rule as a chip beside the company name, so a glance down the
// column shows who is capped. Derived from the plan rather than read from
// the stored employee_limit, because that stored number is ignored on the
// unlimited plans — printing it would report a cap nothing enforces.
const allowanceLabel = (plan?: string | null, status?: string | null) =>
  plan === "starter" || status === "sales_pending" ? "2 allowed" : "Unlimited";

// DD-MM-YYYY. An em dash rather than an empty cell, so a missing date
// looks answered instead of looking like the table failed to load.
const fmtDate = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (v: number) => String(v).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
};

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------
const GB = 1024 ** 3;

// Bytes as a short human string. Only ever used for display, so it rounds; the
// number actually sent is always rebuilt from the GB field the superadmin typed.
const fmtBytes = (n?: number | null) => {
  if (n == null) return "—";
  if (n < 0) return "Unlimited";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 10 || Number.isInteger(v) ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
};

// One editable ceiling. "off" is a real, distinct setting — it ungoverns the
// product rather than setting it to zero, which would block it for everyone.
type CeilingMode = "off" | "limit" | "unlimited";

type CreditsDraft = {
  lizzyMode: Exclude<CeilingMode, "unlimited">;
  lizzy: string;
  meetlyMode: Exclude<CeilingMode, "unlimited">;
  meetly: string;
  dbmsMode: CeilingMode;
  // In GB, because a superadmin thinks in GB and nobody should type 53687091200.
  dbmsGb: string;
};

const EMPTY_CREDITS_DRAFT: CreditsDraft = {
  lizzyMode: "off",
  lizzy: "",
  meetlyMode: "off",
  meetly: "",
  dbmsMode: "off",
  dbmsGb: "",
};

// Seed the form from what is stored. A null ceiling comes back as "off" with an
// empty box, so turning it on starts blank rather than pre-filled with a number
// nobody chose.
const draftFromCredits = (c: AdminCredits): CreditsDraft => ({
  lizzyMode: c.lizzy.limit == null ? "off" : "limit",
  lizzy: c.lizzy.limit == null ? "" : String(c.lizzy.limit),
  meetlyMode: c.meetly.limit == null ? "off" : "limit",
  meetly: c.meetly.limit == null ? "" : String(c.meetly.limit),
  dbmsMode:
    c.dbms.limit_bytes == null
      ? "off"
      : c.dbms.limit_bytes < 0
      ? "unlimited"
      : "limit",
  dbmsGb:
    c.dbms.limit_bytes == null || c.dbms.limit_bytes < 0
      ? ""
      : String(
          // Keep one decimal only when it is actually needed, so a 50 GB grant
          // reads "50" and not "50.0".
          Number((c.dbms.limit_bytes / GB).toFixed(1))
        ),
});

// One product's ceiling, as a card: the mode pills, the number box, and what
// the workspace has actually spent. "No limit" is deliberately a first-class
// choice next to a number rather than an empty box — a blank field that means
// "ungoverned" and a blank field that means "I haven't typed it yet" look
// identical, and only one of them should save.
function CeilingCard({
  title,
  blurb,
  unit,
  mode,
  onMode,
  value,
  onValue,
  usage,
  allowUnlimited = false,
  disabled = false,
  hint,
}: {
  title: string;
  blurb: string;
  unit: string;
  mode: CeilingMode;
  onMode: (m: CeilingMode) => void;
  value: string;
  onValue: (v: string) => void;
  usage: ReactNode;
  allowUnlimited?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
}) {
  const modes: { value: CeilingMode; label: string }[] = [
    { value: "off", label: "No limit" },
    { value: "limit", label: "Limit" },
    ...(allowUnlimited
      ? [{ value: "unlimited" as CeilingMode, label: "Unlimited" }]
      : []),
  ];

  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">{blurb}</p>
        </div>
        <div className="flex shrink-0 rounded-lg bg-gray-100 p-0.5">
          {modes.map((m) => (
            <button
              key={m.value}
              type="button"
              disabled={disabled}
              onClick={() => onMode(m.value)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md transition-colors disabled:opacity-50 ${
                mode === m.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {mode === "limit" && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={value}
            disabled={disabled}
            onChange={(e) => onValue(e.target.value)}
            placeholder="0"
            className="w-32 h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
          />
          <span className="text-xs text-gray-500">{unit}</span>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-2.5">{usage}</p>
      {hint}
    </div>
  );
}


const PLAN_FILTER_OPTIONS: FilterOption[] = [
  { value: "all", label: "All" },
  { value: "starter", label: "Free" },
  { value: "professional", label: "Professional" },
  { value: "enterprise", label: "Custom" },
  { value: "none", label: "No plan" },
];

const STATUS_FILTER_OPTIONS: FilterOption[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Deactivated" },
];


export default function SuperadminConsole() {
  const { user, logout } = useAuth();

  // Which half of the console is on screen. "workspaces" is everything this
  // page has always been; "lio" is the cross-org queue of questions Lio
  // couldn't answer, which has no other home — see LioIssueQueue.tsx.
  const [view, setView] = useState<"workspaces" | "lio">("workspaces");

  const [admins, setAdmins] = useState<SuperadminAdmin[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [search, setSearch] = useState("");

  // Filters + pagination.
  const [planFilter, setPlanFilter] = useState<
    "all" | "starter" | "professional" | "enterprise" | "none"
  >("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">(
    "all"
  );
  const [companyFilter, setCompanyFilter] = useState<string>(""); // "" = all
  const [page, setPage] = useState(1);
  // Rows are far shorter than cards were, so a page holds more of them.
  const PAGE_SIZE = 10;

  // Plan modal state.
  const [planOpen, setPlanOpen] = useState(false);
  const [planTargetId, setPlanTargetId] = useState<string | null>(null);
  const [planDraft, setPlanDraft] = useState<WorkspacePlan>("starter");
  const [planInfo, setPlanInfo] = useState<AdminSubscription | null>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  // Professional means "sees everything", so upgrading also clears any custom
  // panel matrix by default — unchecking keeps the restrictions in place.
  const [grantFullAccess, setGrantFullAccess] = useState(true);

  // Credits modal.
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditsTargetId, setCreditsTargetId] = useState<string | null>(null);
  const [creditsInfo, setCreditsInfo] = useState<AdminCredits | null>(null);
  const [creditsDraft, setCreditsDraft] = useState<CreditsDraft>(EMPTY_CREDITS_DRAFT);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [savingCredits, setSavingCredits] = useState(false);

  // Access modal state.
  const [accessOpen, setAccessOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, PanelPermission>>({});
  const [governed, setGoverned] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete confirmation.
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // Reset password: for an admin whose password and mailbox both fail.
  const [resetTarget, setResetTarget] = useState<SuperadminAdmin | null>(null);
  const [lockTarget, setLockTarget] = useState<SuperadminAdmin | null>(null);
  const [locking, setLocking] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const selected = useMemo(
    () => admins.find((a) => a.user_id === selectedId) || null,
    [admins, selectedId]
  );

  const creditsTarget = useMemo(
    () => admins.find((a) => a.user_id === creditsTargetId) || null,
    [admins, creditsTargetId]
  );

  // The one thing that can be wrong with the DBMS ceiling and nothing else:
  // storage is enforced by a different service with its own database, so the
  // number saved here and the number actually applied can disagree. Two causes,
  // and they need different people, so they are not collapsed into one message.
  const dbmsWarning = useMemo(() => {
    const dbms = creditsInfo?.dbms;
    if (!dbms) return null;
    if (!dbms.reachable) {
      return `${
        dbms.error || "The document service didn't answer."
      } A limit saved now is stored but not applied until it can be reached.`;
    }
    // Reached it, but it is holding a different number — a push that failed
    // earlier. Saving again retries it.
    if ((dbms.limit_bytes ?? null) !== (dbms.quota_bytes ?? null)) {
      return `The document service is currently enforcing ${fmtBytes(
        dbms.quota_bytes
      )}. Save again to push this limit across.`;
    }
    return null;
  }, [creditsInfo]);

  const planTarget = useMemo(
    () => admins.find((a) => a.user_id === planTargetId) || null,
    [admins, planTargetId]
  );

  // A free/starter (or sales-pending) workspace is "limited" — only the
  // free-plan panels are actually available to that admin (mirrors the app's
  // workspace_mode = "limited" rule). We restrict the grid to that plan so the
  // superadmin only sees/toggles what the admin can really use.
  const isLimitedAdmin = (a?: SuperadminAdmin | null) =>
    !!a &&
    ((a.plan || "").toLowerCase() === "starter" ||
      (a.subscription_status || "").toLowerCase() === "sales_pending");

  const planCatalog = useMemo(
    () => panelCatalogForPlan(isLimitedAdmin(selected)),
    [selected]
  );

  const loadAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      setAdmins(await listAdmins());
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load admins.");
    } finally {
      setLoadingAdmins(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [search, planFilter, companyFilter, statusFilter]);

  const openAccess = useCallback(async (admin: SuperadminAdmin) => {
    setSelectedId(admin.user_id);
    setAccessOpen(true);
    setLoadingPerms(true);
    try {
      const res = await getAdminPermissions(admin.user_id);
      // Restrict everything to the admin's PLAN catalog (a free/starter
      // workspace only has the free-plan panels).
      const pc = panelCatalogForPlan(isLimitedAdmin(admin));
      // Open the grid reflecting the admin's REAL current access:
      //  - governed → their saved matrix.
      //  - not governed → full access WITHIN their plan (the permissive
      //    fallback lets them use every panel their plan allows).
      const base = res.governed
        ? blankPermissions(res.permissions)
        : fullAccessPermissions();
      setDraft(restrictPermissionsToCatalog(base, pc));
      setGoverned(res.governed);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load permissions.");
      setDraft(
        restrictPermissionsToCatalog(
          fullAccessPermissions(),
          panelCatalogForPlan(isLimitedAdmin(admin))
        )
      );
      setGoverned(false);
    } finally {
      setLoadingPerms(false);
    }
  }, []);

  const setVisible = (key: string, visible: boolean) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], visible } }));
  const setLevel = (key: string, level: PanelLevel) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], level } }));

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      // Never persist panels outside the admin's plan.
      await setAdminPermissions(
        selectedId,
        restrictPermissionsToCatalog(draft, planCatalog)
      );
      setGoverned(true);
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === selectedId ? { ...a, governed: true } : a))
      );
      toast.success("Panel access saved. Applies on the admin's next page load.");
      setAccessOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await clearAdminPermissions(selectedId);
      setGoverned(false);
      setDraft(restrictPermissionsToCatalog(fullAccessPermissions(), planCatalog));
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === selectedId ? { ...a, governed: false } : a))
      );
      toast.success("Stopped governing — this admin now sees everything again.");
      setAccessOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't clear access.");
    } finally {
      setSaving(false);
    }
  };

  const openCredits = useCallback(async (admin: SuperadminAdmin) => {
    setCreditsTargetId(admin.user_id);
    setCreditsOpen(true);
    setCreditsInfo(null);
    setCreditsDraft(EMPTY_CREDITS_DRAFT);
    setLoadingCredits(true);
    try {
      const credits = await getAdminCredits(admin.user_id);
      setCreditsInfo(credits);
      setCreditsDraft(draftFromCredits(credits));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load the credits.");
    } finally {
      setLoadingCredits(false);
    }
  }, []);

  const handleSaveCredits = async () => {
    if (!creditsTargetId || !creditsInfo) return;

    // Validate before sending: an empty or nonsense box with the ceiling turned
    // ON would otherwise be saved as 0, which blocks the product outright.
    const num = (raw: string, max: number, label: string): number | null => {
      const v = Number(raw.trim());
      if (!raw.trim() || !Number.isFinite(v) || v < 0 || !Number.isInteger(v)) {
        toast.error(`Enter a whole number of ${label}, or turn the limit off.`);
        return null;
      }
      if (v > max) {
        toast.error(`${label} cannot be more than ${max.toLocaleString()}.`);
        return null;
      }
      return v;
    };

    const changes: {
      lizzy_monthly_credits?: number | null;
      meetly_monthly_credits?: number | null;
      dbms_quota_bytes?: number | null;
    } = {};

    if (creditsDraft.lizzyMode === "off") {
      changes.lizzy_monthly_credits = null;
    } else {
      const v = num(creditsDraft.lizzy, creditsInfo.max.lizzy, "AI messages");
      if (v === null) return;
      changes.lizzy_monthly_credits = v;
    }

    if (creditsDraft.meetlyMode === "off") {
      changes.meetly_monthly_credits = null;
    } else {
      const v = num(creditsDraft.meetly, creditsInfo.max.meetly, "meetings");
      if (v === null) return;
      changes.meetly_monthly_credits = v;
    }

    if (creditsDraft.dbmsMode === "off") {
      changes.dbms_quota_bytes = null;
    } else if (creditsDraft.dbmsMode === "unlimited") {
      changes.dbms_quota_bytes = -1;
    } else {
      const gb = Number(creditsDraft.dbmsGb.trim());
      if (!creditsDraft.dbmsGb.trim() || !Number.isFinite(gb) || gb < 0) {
        toast.error("Enter the storage allowance in GB, or turn the limit off.");
        return;
      }
      const bytes = Math.round(gb * GB);
      if (bytes > creditsInfo.max.dbms_bytes) {
        toast.error(
          `Storage cannot be more than ${fmtBytes(creditsInfo.max.dbms_bytes)}.`
        );
        return;
      }
      changes.dbms_quota_bytes = bytes;
    }

    setSavingCredits(true);
    try {
      const res = await setAdminCredits(creditsTargetId, changes);
      setCreditsInfo(res);
      setCreditsDraft(draftFromCredits(res));
      // The DBMS ceiling is the only one that has to cross into another service.
      // A failed push leaves the other two applied, so say so rather than
      // reporting a clean save.
      if (changes.dbms_quota_bytes !== undefined && !res.dbms.reachable) {
        toast.warning(
          res.dbms.error ||
            "Saved, but the storage limit hasn't reached the document service yet. Save again to retry."
        );
      } else {
        toast.success("Credits updated. It applies to the next message, meeting or upload.");
        setCreditsOpen(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't update the credits.");
    } finally {
      setSavingCredits(false);
    }
  };

  const openPlan = useCallback(async (admin: SuperadminAdmin) => {
    setPlanTargetId(admin.user_id);
    setPlanOpen(true);
    setPlanInfo(null);
    // Seed from the list row so the dialog is never empty, then confirm with
    // the live subscription (which also carries status / employee limit).
    setPlanDraft(((admin.plan || "starter").toLowerCase() as WorkspacePlan));
    setGrantFullAccess(true);
    setLoadingPlan(true);
    try {
      const sub = await getAdminSubscription(admin.user_id);
      setPlanInfo(sub);
      if (sub.plan) setPlanDraft(sub.plan);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't load the plan.");
    } finally {
      setLoadingPlan(false);
    }
  }, []);

  const handleSavePlan = async () => {
    if (!planTargetId) return;
    setSavingPlan(true);
    try {
      const res = await setAdminPlan(planTargetId, planDraft, {
        grantFullAccess: planDraft === "professional" && grantFullAccess,
      });
      setAdmins((prev) =>
        prev.map((a) =>
          a.user_id === planTargetId
            ? {
                ...a,
                plan: res.plan,
                subscription_status: res.status,
                governed: res.governed,
              }
            : a
        )
      );
      setPlanInfo(res);
      toast.success(
        `Plan set to ${planBadge(res.plan).label}. Applies on the admin's next sign-in.`
      );
      setPlanOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't change the plan.");
    } finally {
      setSavingPlan(false);
    }
  };

  // Irreversible, and it takes the admin's whole company with it — so the
  // dialog spells that out rather than asking a bare "are you sure?".
  const confirmDelete = (admin: SuperadminAdmin) =>
    setConfirm({
      title: `Delete ${admin.name} and their workspace?`,
      tone: "danger",
      confirmLabel: "Delete permanently",
      description: (
        <div className="space-y-2">
          <p>
            <span className="font-semibold">
              {admin.company_name || admin.email}
            </span>{" "}
            will be erased from the platform: this admin, every employee under
            it, their logins, and all attendance, payroll and company records.
          </p>
          <p className="text-red-600 font-semibold">
            This cannot be undone.
          </p>
          <p className="text-gray-500">
            To lock the account without destroying anything, deactivate it
            instead.
          </p>
        </div>
      ),
      onConfirm: async () => {
        try {
          const res = await deleteAdmin(admin.user_id);
          setAdmins((prev) =>
            prev.filter((a) => a.user_id !== admin.user_id)
          );
          toast.success(
            `${admin.name} deleted — ${res.users} account${
              res.users === 1 ? "" : "s"
            } and ${res.rows} record${res.rows === 1 ? "" : "s"} removed.`
          );
        } catch (err: any) {
          toast.error(
            err?.response?.data?.detail || "Couldn't delete this workspace."
          );
        }
      },
    });

  const toggleActive = async (admin: SuperadminAdmin) => {
    const next = !admin.is_active;
    try {
      await setAdminStatus(admin.user_id, next);
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === admin.user_id ? { ...a, is_active: next } : a))
      );
      toast.success(next ? "Admin activated." : "Admin deactivated.");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't change status.");
    }
  };

  // Shut an admin's sign-in without ending their account. Confirmed, because
  // unlike the three-wrong-passwords lock this one also shuts 'Forgot
  // password': the admin cannot undo it from their side at all.
  const handleLock = async () => {
    if (!lockTarget) return;
    try {
      setLocking(true);
      const res = await lockLogin(lockTarget.email);
      setAdmins((prev) =>
        prev.map((a) =>
          a.user_id === lockTarget.user_id ? { ...a, sign_in_locked: true } : a
        )
      );
      toast.success(res.message || "Sign-in locked.");
      setLockTarget(null);
    } catch (err: any) {
      toast.error(err?.message || "Couldn't lock sign-in.");
    } finally {
      setLocking(false);
    }
  };

  const handleUnlock = async (admin: SuperadminAdmin) => {
    try {
      const res = await unlockLogin(admin.email);
      setAdmins((prev) =>
        prev.map((a) => (a.user_id === admin.user_id ? { ...a, sign_in_locked: false } : a))
      );
      toast.success(res.message || "Sign-in unlocked.");
    } catch (err: any) {
      toast.error(err?.message || "Couldn't unlock sign-in.");
    }
  };

  const openReset = (admin: SuperadminAdmin) => {
    setResetTarget(admin);
    setResetPw("");
    setResetConfirm("");
    setShowResetPw(false);
    setShowResetConfirm(false);
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if (resetPw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (resetPw !== resetConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setResetting(true);
    try {
      await resetAdminPassword(resetTarget.user_id, resetPw);
      setAdmins((prev) =>
        prev.map((a) =>
          a.user_id === resetTarget.user_id ? { ...a, sign_in_locked: false } : a
        )
      );
      toast.success(
        "Password reset. Share it with the admin — they can change it later from Profile."
      );
      setResetTarget(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't reset the password.");
    } finally {
      setResetting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return admins.filter((a) => {
      if (
        q &&
        !(
          a.name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          (a.company_name || "").toLowerCase().includes(q)
        )
      )
        return false;
      if (companyFilter && (a.company_name || "") !== companyFilter)
        return false;
      if (statusFilter !== "all" && a.is_active !== (statusFilter === "active"))
        return false;
      if (planFilter !== "all") {
        const p = (a.plan || "").toLowerCase();
        if (planFilter === "none" ? !!p : p !== planFilter) return false;
      }
      return true;
    });
  }, [admins, search, planFilter, companyFilter, statusFilter]);

  // Pagination — PAGE_SIZE rows per page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageClamped = Math.min(page, totalPages);
  const paged = filtered.slice(
    (pageClamped - 1) * PAGE_SIZE,
    pageClamped * PAGE_SIZE
  );

  // Distinct companies for the Companies dropdown. Admins with no company
  // profile yet simply have nothing to offer here. "" is the All row.
  const companyOptions = useMemo<FilterOption[]>(() => {
    const names = Array.from(
      new Set(admins.map((a) => a.company_name || "").filter(Boolean))
    ).sort();
    return [
      { value: "", label: "All" },
      ...names.map((c) => ({ value: c, label: c })),
    ];
  }, [admins]);

  const grantedCount = enabledCountForCatalog(draft, planCatalog);

  // Workspace plan → { label, badge classes }. starter=Free, professional,
  // enterprise=Custom. Unknown/none reads as "No plan".
  const planBadge = (plan?: string | null) => {
    const p = (plan || "").toLowerCase();
    if (p === "starter")
      return { label: "Free", cls: "bg-slate-100 text-slate-600" };
    if (p === "professional")
      return { label: "Professional", cls: "bg-blue-100 text-blue-700" };
    if (p === "enterprise")
      return { label: "Custom", cls: "bg-amber-100 text-amber-700" };
    return { label: "No plan", cls: "bg-gray-100 text-gray-400" };
  };

  return (
    // The console owns the viewport: nothing here scrolls except the
    // table body, so the toolbar and the column labels never leave.
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="shrink-0 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-bold text-gray-900 leading-tight">
                Superadmin Console
              </h1>
              <p className="text-[11px] text-gray-500 truncate">
                {user?.email} · governs {admins.length} admin
                {admins.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Two views, one console. A separate route would need its own
                superadmin guard and its own admin fetch; the queue's product-org
                picker needs that same admin list anyway. */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-gray-100">
              {([
                { key: "workspaces", label: "Workspaces" },
                { key: "lio", label: "Lio issues" },
              ] as const).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setView(t.key)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                    view === t.key
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => logout()}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 w-full max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {view === "lio" ? (
          // The queue needs the admin list for its product-org picker, so it
          // takes the one this page already loaded rather than fetching again.
          <LioIssueQueue admins={admins} />
        ) : (
        <>
        {/* Everything on one card: filters, rows and paging. Cards showed
            four facts per admin and hid the rest; a table shows a dozen and
            lets the superadmin read down a column to compare tenants. */}
        <div className="h-full flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm">
          {/* Toolbar. `relative z-30` is what keeps an open filter panel on
              top of the table: the scroller below is a later sibling, so at
              equal z-index its sticky emerald column heads painted over the
              list hanging down into them. */}
          <div className="relative z-30 shrink-0 p-4 flex flex-col sm:flex-row sm:items-center gap-4 border-b border-gray-100">
            <div className="relative flex-1 min-w-0 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search user name or email"
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
            </div>

            {/* Companies — searchable rather than a fixed list, because the
                tenant list grows without bound. */}
            <FilterDropdown
              label="Companies"
              value={companyFilter}
              onChange={setCompanyFilter}
              options={companyOptions}
              widthClass="sm:w-56"
              searchable
              searchPlaceholder="Search company…"
              emptyText="No matching company."
              icon={<Building2 className="w-4 h-4 shrink-0 text-emerald-600" />}
            />

            {/* Plan filter */}
            <FilterDropdown
              label="Plan"
              value={planFilter}
              onChange={(v) => setPlanFilter(v as typeof planFilter)}
              options={PLAN_FILTER_OPTIONS}
              widthClass="sm:w-44"
              icon={<CreditCard className="w-4 h-4 shrink-0 text-emerald-600" />}
            />

            {/* Status filter */}
            <FilterDropdown
              label="Status"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as typeof statusFilter)}
              options={STATUS_FILTER_OPTIONS}
              widthClass="sm:w-40"
              icon={
                <SlidersHorizontal className="w-4 h-4 shrink-0 text-emerald-600" />
              }
            />
          </div>

          {/* The only scroller on the page — both axes. The table is wider
              than most screens on purpose: every column is a real field, so
              it scrolls sideways rather than dropping any of them. */}
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr>
                  {[
                    "User Name",
                    "Email",
                    "Mobile No",
                    "Company Name",
                    "Created Date",
                    "State",
                    "Plan",
                    "Trial Ends",
                    "Subscription End",
                    "Last Login",
                    "Access",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      // Sticky cells, not a sticky row: a <tr> cannot carry the
                      // background once its cells are lifted, and a see-through
                      // header shows the rows sliding underneath it. The
                      // `!` utilities outrank the global `.gt-themed thead th`
                      // rule, which greys and uppercases every table heading.
                      className="sticky top-0 z-20 bg-emerald-100 px-4 py-3.5 text-xs font-bold !text-emerald-900 !normal-case !tracking-normal whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="sticky top-0 z-20 bg-emerald-100 px-4 py-3.5 w-12">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingAdmins ? (
                  <tr>
                    <td colSpan={13} className="py-20">
                      <div className="flex justify-center text-gray-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="py-20 text-center text-gray-400">
                      <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No admins found.</p>
                    </td>
                  </tr>
                ) : (
                  paged.map((a) => {
                    const pb = planBadge(a.plan);
                    return (
                      <tr
                        key={a.user_id}
                        className={`hover:bg-gray-50/80 transition-colors ${
                          a.is_active ? "" : "bg-red-50/30"
                        }`}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-200 to-indigo-300 flex items-center justify-center shrink-0 text-xs font-bold text-indigo-800">
                              {(a.name || a.email).charAt(0).toUpperCase()}
                            </span>
                            <span className="text-sm font-semibold text-gray-900 truncate max-w-[11rem]">
                              {a.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {a.email}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {a.mobile || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {a.company_name ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-sm text-gray-700 truncate max-w-[14rem]">
                                {a.company_name}
                              </span>
                              <span
                                className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full shrink-0"
                                title="Employees this workspace may add"
                              >
                                {allowanceLabel(a.plan, a.subscription_status)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(a.created_at) || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {a.state || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pb.cls}`}
                          >
                            {pb.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(a.trial_end) || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(a.subscription_end) || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                          {fmtDate(a.last_login_at) || (
                            <span className="text-gray-300">Never</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              a.governed
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {a.governed ? "Governed" : "Full access"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              a.is_active
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {a.is_active ? "Active" : "Deactivated"}
                          </span>
                          {a.sign_in_locked && (
                            <span className="ml-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                              Sign-in locked
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                aria-label={`Actions for ${a.name}`}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="bg-white w-56 p-1.5"
                            >
                              <DropdownMenuItem
                                onClick={() => openPlan(a)}
                                className="gap-2.5 rounded-lg py-2 cursor-pointer"
                              >
                                <span className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                  <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                                </span>
                                <span className="text-[13px] text-gray-700">
                                  Change plan
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openCredits(a)}
                                className="gap-2.5 rounded-lg py-2 cursor-pointer"
                              >
                                <span className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                  <Coins className="w-3.5 h-3.5 text-emerald-600" />
                                </span>
                                <span className="text-[13px] text-gray-700">
                                  Credits
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openAccess(a)}
                                className="gap-2.5 rounded-lg py-2 cursor-pointer"
                              >
                                <span className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                  <SlidersHorizontal className="w-3.5 h-3.5 text-blue-600" />
                                </span>
                                <span className="text-[13px] text-gray-700">
                                  Panel access
                                </span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => toggleActive(a)}
                                className="gap-2.5 rounded-lg py-2 cursor-pointer"
                              >
                                {a.is_active ? (
                                  <>
                                    <span className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                                      <XCircle className="w-3.5 h-3.5 text-red-600" />
                                    </span>
                                    <span className="text-[13px] text-gray-700">
                                      Deactivate admin
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                    </span>
                                    <span className="text-[13px] text-gray-700">
                                      Activate admin
                                    </span>
                                  </>
                                )}
                              </DropdownMenuItem>
                              {!a.sign_in_locked && (
                                <DropdownMenuItem
                                  onClick={() => setLockTarget(a)}
                                  className="gap-2.5 rounded-lg py-2 cursor-pointer"
                                >
                                  <span className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                                    <Lock className="w-3.5 h-3.5 text-red-600" />
                                  </span>
                                  <span className="text-[13px] text-gray-700">
                                    Lock sign-in
                                  </span>
                                </DropdownMenuItem>
                              )}
                              {a.sign_in_locked && (
                                <DropdownMenuItem
                                  onClick={() => handleUnlock(a)}
                                  className="gap-2.5 rounded-lg py-2 cursor-pointer"
                                >
                                  <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                    <LockOpen className="w-3.5 h-3.5 text-amber-600" />
                                  </span>
                                  <span className="text-[13px] text-gray-700">
                                    Unlock sign-in
                                  </span>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => openReset(a)}
                                className="gap-2.5 rounded-lg py-2 cursor-pointer"
                              >
                                <span className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                                  <KeyRound className="w-3.5 h-3.5 text-amber-600" />
                                </span>
                                <span className="text-[13px] text-gray-700">
                                  Reset password
                                </span>
                              </DropdownMenuItem>
                              {/* Last, and set apart: the only item here that
                                  cannot be undone. */}
                              <DropdownMenuItem
                                onClick={() => confirmDelete(a)}
                                className="gap-2.5 rounded-lg py-2 mt-1 border-t border-gray-100 cursor-pointer"
                              >
                                <span className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                </span>
                                <span className="text-[13px] font-semibold text-red-600">
                                  Delete user
                                </span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Count + paging */}
          {!loadingAdmins && filtered.length > 0 && (
            <div className="shrink-0 px-4 py-3.5 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-[11px] text-gray-400">
                Showing {(pageClamped - 1) * PAGE_SIZE + 1}–
                {Math.min(pageClamped * PAGE_SIZE, filtered.length)} of{" "}
                {filtered.length} admin{filtered.length === 1 ? "" : "s"}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pageClamped <= 1}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="text-xs text-gray-500 font-medium">
                    Page {pageClamped} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={pageClamped >= totalPages}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Credits modal — the workspace-wide ceilings. Unlike the plan these
          bite immediately: every one is read from the database on the spend
          path, so lowering one stops the next message, meeting or upload. */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">
              {creditsTarget ? `${creditsTarget.name} — Credits` : "Credits"}
            </DialogTitle>
            {creditsTarget && (
              <p className="text-xs text-gray-500">
                {creditsTarget.email}
                {creditsTarget.company_name ? ` · ${creditsTarget.company_name}` : ""}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-0.5">
              {loadingCredits
                ? "Loading current allowances…"
                : creditsInfo?.granted
                ? "What this whole workspace may spend a month. The admin allocates inside it and cannot raise it."
                : "No allowances set — this workspace is unmetered above its per-person limits."}
            </p>
          </DialogHeader>

          {loadingCredits ? (
            <div className="py-20 flex justify-center text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : !creditsInfo ? (
            <div className="px-5 py-10 text-center text-sm text-gray-400">
              Couldn't load this workspace's credits.
            </div>
          ) : (
            <div className="px-5 py-4 space-y-3">
              <CeilingCard
                title="Lio AI messages"
                blurb="One message is one credit, however many model calls it takes."
                unit="messages / month"
                mode={creditsDraft.lizzyMode}
                onMode={(m) =>
                  setCreditsDraft((d) => ({
                    ...d,
                    lizzyMode: m as CreditsDraft["lizzyMode"],
                  }))
                }
                value={creditsDraft.lizzy}
                onValue={(v) => setCreditsDraft((d) => ({ ...d, lizzy: v }))}
                disabled={savingCredits}
                usage={
                  creditsInfo.lizzy.used == null
                    ? "Usage this month is unavailable."
                    : `${creditsInfo.lizzy.used.toLocaleString()} used this month across the workspace.`
                }
              />

              <CeilingCard
                title="Meetly meetings"
                blurb="One completed meeting is one credit; a meeting still processing holds one."
                unit="meetings / month"
                mode={creditsDraft.meetlyMode}
                onMode={(m) =>
                  setCreditsDraft((d) => ({
                    ...d,
                    meetlyMode: m as CreditsDraft["meetlyMode"],
                  }))
                }
                value={creditsDraft.meetly}
                onValue={(v) => setCreditsDraft((d) => ({ ...d, meetly: v }))}
                disabled={savingCredits}
                usage={
                  creditsInfo.meetly.used == null
                    ? "Usage this month is unavailable."
                    : `${creditsInfo.meetly.used.toLocaleString()} completed${
                        creditsInfo.meetly.reserved
                          ? `, ${creditsInfo.meetly.reserved} in progress`
                          : ""
                      } this month across the workspace.`
                }
              />

              <CeilingCard
                title="DBMS storage"
                blurb="Documents plus the chat files everyone in the workspace has sent."
                unit="GB"
                mode={creditsDraft.dbmsMode}
                onMode={(m) => setCreditsDraft((d) => ({ ...d, dbmsMode: m }))}
                value={creditsDraft.dbmsGb}
                onValue={(v) => setCreditsDraft((d) => ({ ...d, dbmsGb: v }))}
                allowUnlimited
                disabled={savingCredits}
                usage={
                  creditsInfo.dbms.reachable
                    ? `${fmtBytes(creditsInfo.dbms.used_bytes)} held across ${
                        creditsInfo.dbms.account_count ?? 0
                      } account${creditsInfo.dbms.account_count === 1 ? "" : "s"}.`
                    : // Why it is unavailable belongs in the one warning below,
                      // not repeated here in slightly different words.
                      "Usage unavailable."
                }
                hint={
                  // Storage lives in the messenger service's own database, so
                  // "saved" here can be only half the write. Say which number is
                  // actually being enforced rather than showing one and applying
                  // the other.
                  //
                  // Only while a ceiling is actually in play: someone leaving
                  // storage ungoverned has nothing to sync, and warning them
                  // about a service they are not using is pure noise.
                  creditsDraft.dbmsMode !== "off" && dbmsWarning ? (
                    <p className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                      <span>{dbmsWarning}</span>
                    </p>
                  ) : null
                }
              />

              <p className="text-[11px] text-gray-400 pt-1">
                "No limit" leaves that product to its per-person allowances alone.
                A limit of 0 blocks it for the whole workspace.
              </p>
            </div>
          )}

          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              onClick={() => setCreditsOpen(false)}
              disabled={savingCredits}
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCredits}
              disabled={savingCredits || loadingCredits || !creditsInfo}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {savingCredits ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save credits
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workspace plan modal */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">
              {planTarget ? `${planTarget.name} — Workspace plan` : "Workspace plan"}
            </DialogTitle>
            {planTarget && (
              <p className="text-xs text-gray-500">
                {planTarget.email}
                {planTarget.company_name ? ` · ${planTarget.company_name}` : ""}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-0.5">
              {loadingPlan
                ? "Loading current plan…"
                : planInfo?.plan
                ? `Currently ${planBadge(planInfo.plan).label} · ${
                    planInfo.status ?? "—"
                  } · ${employeeLimitLabel(planInfo.plan, planInfo.status)}`
                : "No subscription yet — picking a plan creates one."}
            </p>
          </DialogHeader>

          <div className="px-5 py-4 space-y-2">
            {PLAN_OPTIONS.map((opt) => {
              const active = planDraft === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPlanDraft(opt.value)}
                  disabled={loadingPlan || savingPlan}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-colors disabled:opacity-60 ${
                    active
                      ? "border-indigo-500 bg-indigo-50/60 ring-1 ring-indigo-200"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {opt.label}
                    </span>
                    <span className="flex items-center gap-2">
                      {planInfo?.plan === opt.value && (
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                          Current
                        </span>
                      )}
                      {active && <Check className="w-4 h-4 text-indigo-600" />}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-0.5">{opt.blurb}</p>
                </button>
              );
            })}

            {/* Professional means "sees everything" — offer to drop any custom
                panel restrictions the admin currently has. */}
            {planDraft === "professional" && (
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={grantFullAccess}
                  onChange={(e) => setGrantFullAccess(e.target.checked)}
                  className="mt-0.5 w-3.5 h-3.5 accent-indigo-600"
                />
                <span className="text-[11px] text-gray-500">
                  Also give full panel access — removes any custom panel
                  restrictions set for this admin.
                </span>
              </label>
            )}

            <p className="text-[11px] text-gray-400 pt-1">
              The new plan reaches the admin's app on their next sign-in.
            </p>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              onClick={() => setPlanOpen(false)}
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSavePlan}
              disabled={savingPlan || loadingPlan}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingPlan ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save plan
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access panel modal */}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-bold text-gray-900">
              {selected ? `${selected.name} — Panel access` : "Panel access"}
            </DialogTitle>
            {selected && (
              <p className="text-xs text-gray-500">
                {selected.email}
                {selected.company_name ? ` · ${selected.company_name}` : ""}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-0.5">
              {governed
                ? `${grantedCount} panel${grantedCount === 1 ? "" : "s"} enabled`
                : "Not governed yet — this admin currently sees everything."}
            </p>
          </DialogHeader>

          <div className="px-5 py-4">
            {loadingPerms ? (
              <div className="py-16 flex justify-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <PermissionGridEditor
                permissions={draft}
                setVisible={setVisible}
                setLevel={setLevel}
                panelCatalog={planCatalog}
              />
            )}
          </div>

          <div className="px-5 py-3 border-t border-gray-100 sticky bottom-0 bg-white flex items-center justify-end gap-2">
            {governed && (
              <button
                onClick={handleClear}
                disabled={saving || loadingPerms}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" /> Reset to full access
              </button>
            )}
            <button
              onClick={() => setAccessOpen(false)}
              className="text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loadingPerms}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save access
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!lockTarget}
        onOpenChange={(open) => {
          if (!open && !locking) setLockTarget(null);
        }}
      >
        <DialogContent className="max-w-md p-0 gap-0">
          {/* One line, same as the Employees screen's — it used to spell out
              every consequence and read as a wall of text. The email stays:
              this list is every admin on the platform and they are easy to
              mix up. */}
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">
              Lock sign-in
            </DialogTitle>
            {lockTarget && (
              <p className="text-xs text-gray-500">{lockTarget.email}</p>
            )}
          </DialogHeader>
          <div className="px-5 pt-4">
            <p className="text-xs text-gray-500">
              They won't be able to sign in until an admin unlocks the account.
            </p>
          </div>
          <div className="px-5 pb-5 pt-4 flex justify-end gap-2">
            <button
              onClick={() => setLockTarget(null)}
              disabled={locking}
              className="h-9 px-4 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleLock}
              disabled={locking}
              className="h-9 px-4 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {locking && <Loader2 className="w-4 h-4 animate-spin" />}
              Lock sign-in
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!resetTarget}
        onOpenChange={(open) => {
          if (!open && !resetting) setResetTarget(null);
        }}
      >
        <DialogContent className="max-w-md p-0 gap-0">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-base font-bold text-gray-900">
              {resetTarget ? `${resetTarget.name} — Reset password` : "Reset password"}
            </DialogTitle>
            {resetTarget && (
              <p className="text-xs text-gray-500">
                {resetTarget.email}
                {resetTarget.company_name ? ` · ${resetTarget.company_name}` : ""}
              </p>
            )}
          </DialogHeader>
          <div className="px-5 py-4 space-y-3">
            <p className="text-xs text-gray-500">
              Sets a new password and unlocks sign-in. Share it with the admin
              yourself; they can change it later from Profile. Anyone signed in
              on this account is signed out.
            </p>
            <div className="relative">
              <input
                type={showResetPw ? "text" : "password"}
                autoComplete="new-password"
                placeholder="New password (min 6 characters)"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                className="w-full h-10 pl-3 pr-10 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button
                type="button"
                onClick={() => setShowResetPw((v) => !v)}
                aria-label={showResetPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
              >
                {showResetPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showResetConfirm ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                className="w-full h-10 pl-3 pr-10 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <button
                type="button"
                onClick={() => setShowResetConfirm((v) => !v)}
                aria-label={showResetConfirm ? "Hide confirm password" : "Show confirm password"}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
              >
                {showResetConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="px-5 pb-5 flex justify-end gap-2">
            <button
              onClick={() => setResetTarget(null)}
              disabled={resetting}
              className="h-9 px-4 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleResetPassword}
              disabled={resetting || !resetPw || !resetConfirm}
              className="h-9 px-4 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {resetting && <Loader2 className="w-4 h-4 animate-spin" />}
              Reset password
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
